if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      variantResponseCache = new Map();
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();
        this.committedOptions = this.variantSelectors?.outerHTML;

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initializeVariantPrefetch();
        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      initializeVariantPrefetch() {
        const picker = this.variantSelectors;
        if (!picker) return;
        const selected = Array.from(picker.querySelectorAll('fieldset input:checked, select option:checked'))
          .map((input) => input.dataset.optionValueId).filter(Boolean);
        if (selected.length) {
          this.variantResponseCache.set(this.buildRequestUrlWithParams(this.dataset.url, selected), {
            timestamp: Date.now(), text: this.outerHTML,
          });
        }
        this.variantIntentHandler = (event) => {
          if (!this.variantSelectors?.contains(event.target)) return;
          const label = event.target.closest('label[for]');
          const input = label ? document.getElementById(label.htmlFor) : event.target;
          if (!input?.dataset.optionValueId) return;
          const group = input.closest('fieldset, select');
          const values = Array.from(this.variantSelectors.querySelectorAll('fieldset, select')).map((field) =>
            field === group ? input.dataset.optionValueId : field.querySelector('input:checked, option:checked')?.dataset.optionValueId
          ).filter(Boolean);
          const url = input.dataset.productUrl || this.dataset.url;
          if (url === this.dataset.url) this.prefetchVariant(this.buildRequestUrlWithParams(url, values));
        };
        this.addEventListener('pointerover', this.variantIntentHandler);
        this.addEventListener('focusin', this.variantIntentHandler);
        this.variantWarmTimer = setTimeout(() => this.warmVariantOptions(), 350);
      }

      async warmVariantOptions() {
        if (!this.isConnected || navigator.connection?.saveData || /(^|-)2g$/.test(navigator.connection?.effectiveType || '')) return;
        const groups = this.variantSelectors?.querySelectorAll('fieldset, select');
        if (groups?.length !== 1) return;
        const inputs = Array.from(groups[0].querySelectorAll('[data-option-value-id]'));
        if (inputs.length > 12) return;
        const urls = [...new Set(inputs.filter((input) => !input.dataset.productUrl || input.dataset.productUrl === this.dataset.url)
          .map((input) => this.buildRequestUrlWithParams(this.dataset.url, [input.dataset.optionValueId])))];
        const worker = async () => {
          while (urls.length && this.isConnected) {
            const url = urls.shift();
            await this.prefetchVariant(url);
          }
        };
        await Promise.all([worker(), worker()]);
      }

      async prefetchVariant(url) {
        try {
          const text = await this.getVariantResponse(url);
          if (!this.isConnected) return;
          const html = new DOMParser().parseFromString(text, 'text/html');
          const entry = this.variantResponseCache.get(url);
          if (!entry || entry.imageWarmed) return;
          entry.imageWarmed = true;
          this.prepareVariantMedia(html, entry.controller?.signal || new AbortController().signal, 'low').catch(() => {});
        } catch {}
      }

      getVariantResponse(url) {
        const existing = this.variantResponseCache.get(url);
        if (existing && Date.now() - existing.timestamp < 60000) {
          return existing.text ? Promise.resolve(existing.text) : existing.promise;
        }
        existing?.controller?.abort();
        this.variantResponseCache.delete(url);
        if (this.variantResponseCache.size >= 16) {
          const key = this.variantResponseCache.keys().next().value;
          this.variantResponseCache.get(key)?.controller?.abort();
          this.variantResponseCache.delete(key);
        }
        const entry = { timestamp: Date.now(), controller: new AbortController() };
        const timer = setTimeout(() => entry.controller.abort(), 12000);
        entry.promise = fetch(url, { signal: entry.controller.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`Product update failed: ${response.status}`);
            return response.text();
          }).then((text) => {
            if (!new DOMParser().parseFromString(text, 'text/html').querySelector('product-info')) {
              throw new Error('Missing product information');
            }
            entry.text = text;
            entry.timestamp = Date.now();
            return text;
          }).catch((error) => {
            if (this.variantResponseCache.get(url) === entry) this.variantResponseCache.delete(url);
            throw error;
          }).finally(() => clearTimeout(timer));
        this.variantResponseCache.set(url, entry);
        return entry.promise;
      }

      waitForVariantResponse(url, signal) {
        return new Promise((resolve, reject) => {
          const abort = () => reject(new DOMException('Selection changed', 'AbortError'));
          if (signal.aborted) return abort();
          signal.addEventListener('abort', abort, { once: true });
          this.getVariantResponse(url).then(resolve, reject).finally(() => signal.removeEventListener('abort', abort));
        });
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.abortController?.abort();
        this.cartUpdateUnsubscriber?.();
        clearTimeout(this.variantWarmTimer);
        this.removeEventListener('pointerover', this.variantIntentHandler);
        this.removeEventListener('focusin', this.variantIntentHandler);
        this.variantResponseCache.forEach((entry) => entry.controller?.abort());
        this.variantResponseCache.clear();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        if (!this.variantLoading && productForm) {
          this.submitState = {
            disabled: productForm.submitButton.hasAttribute('disabled'),
            text: productForm.submitButtonText.textContent,
          };
        }
        this.variantLoading = true;
        productForm?.toggleSubmitButton(true);
        this.setVariantLoading(true);
        productForm?.handleErrorMessage();
      }

      setVariantLoading(loading) {
        const form = this.productForm;
        const button = form?.submitButton;
        if (!button) return;
        if (loading) button.setAttribute('aria-busy', 'true');
        else button.removeAttribute('aria-busy');
        button.classList.toggle('loading', loading);
        form.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
      }

      async prepareVariantMedia(html, signal, priority = 'high') {
        const variant = this.getSelectedVariant(html);
        const id = variant?.featured_media?.id;
        if (!id) return;
        const selector = `[data-media-id="${this.dataset.section}-${id}"] img`;
        const source = this.querySelector(selector) || html.querySelector(selector);
        if (!source) return;
        const image = new Image();
        image.loading = 'eager';
        image.fetchPriority = priority;
        image.sizes = source.sizes;
        image.srcset = source.srcset;
        image.src = source.src;
        await new Promise((resolve) => {
          let timer;
          const finish = () => {
            clearTimeout(timer);
            image.removeEventListener('load', finish);
            image.removeEventListener('error', finish);
            signal.removeEventListener('abort', finish);
            resolve();
          };
          image.addEventListener('load', finish, { once: true });
          image.addEventListener('error', finish, { once: true });
          signal.addEventListener('abort', finish, { once: true });
          timer = setTimeout(finish, 8000);
          if (image.complete || signal.aborted) finish();
        });
        if (!signal.aborted && image.naturalWidth && image.decode) {
          await image.decode().catch(() => {});
        }
        html.bbaMediaReady = Boolean(image.complete && image.naturalWidth);
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const sourceProductInfo = html.querySelector(selector);
          const variant = this.getSelectedVariant(sourceProductInfo);
          this.variantSelectors?.resolvePendingSelectPromise(variant, this.getVariantSelects(sourceProductInfo));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      async renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        const controller = new AbortController();
        this.abortController = controller;
        const timeout = setTimeout(() => controller.abort(), 15000);
        try {
          const responseText = await this.waitForVariantResponse(requestUrl, controller.signal);
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          if (!html.querySelector('product-info')) throw new Error('Missing product information');
          if (this.abortController !== controller) return;
          if (controller.signal.aborted) throw new Error('Product update timed out');
          const sameProduct = this.pendingRequestUrl === null || this.pendingRequestUrl === this.dataset.url;
          if (sameProduct) {
            const media = this.prepareVariantMedia(html, controller.signal);
            html.bbaMediaReady = false;
            media.then(() => {
              if (this.abortController !== controller || controller.signal.aborted || !this.isConnected) return;
              if (html.bbaMediaReady) this.updateMedia(html, this.getSelectedVariant(html)?.featured_media?.id);
            }).catch(() => {});
          }
          callback(html);
          this.committedOptions = this.variantSelectors?.outerHTML;
          this.pendingRequestUrl = null;
          document.getElementById(targetId)?.focus({ preventScroll: true });
        } catch (error) {
          if (this.abortController !== controller || !this.isConnected) return;
          this.variantSelectors?.rejectPendingSelectPromise(error);
          if (this.committedOptions) {
            this.updateOptionValues(new DOMParser().parseFromString(this.committedOptions, 'text/html'));
          }
          this.productForm?.toggleSubmitButton(this.submitState?.disabled ?? true, this.submitState?.text);
          this.productForm?.handleErrorMessage(window.cartStrings?.error || 'Please try again.');
          this.pendingRequestUrl = null;
          document.getElementById(targetId)?.focus({ preventScroll: true });
        } finally {
          clearTimeout(timeout);
          if (this.abortController === controller) {
            this.variantLoading = false;
            this.setVariantLoading(false);
          }
        }
      }

      parseJsonScript(parent, selector) {
        try {
          return JSON.parse(parent?.querySelector(selector)?.textContent);
        } catch {
          return null;
        }
      }

      getVariantSelects(queryRoot) {
        return queryRoot?.querySelector('variant-selects');
      }

      getSelectedVariant(productInfoNode) {
        return this.parseJsonScript(this.getVariantSelects(productInfoNode), '[data-selected-variant]');
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const sourceVariantSelects = this.getVariantSelects(html);
          const variant = this.getSelectedVariant(html);

          // Resolve product:select promise before updateOptionValues replaces the variant-selects DOM element
          this.variantSelectors?.resolvePendingSelectPromise(variant, sourceVariantSelects);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          if (html.bbaMediaReady !== false) this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          for (const attribute of ['data-bba-pack-gift', 'data-bba-pack-total']) {
            this.querySelectorAll(`[${attribute}]`).forEach((target) => {
              const source = html.querySelector(`[${attribute}="${target.getAttribute(attribute)}"]`);
              if (source) target.innerHTML = source.innerHTML;
            });
          }

          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;
        const reveal = this.querySelector('media-gallery')?.captureVariantFrame?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`
        );
        try {
          this.commitVariantMedia(html, variantFeaturedMediaId);
        } finally {
          reveal?.();
        }
      }

      commitVariantMedia(html, variantFeaturedMediaId) {

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              const item = mediaGalleryDestinationItems[i];
              item.classList.remove('scroll-trigger', 'animate--fade-in');
              mediaGallerySource.prepend(item);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true,
          { preserveScroll: true }
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

